import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { askCopilotAbout } from '../lib/nav';
import Icon from './Icon';

/* Files in chats (groups and direct messages). The file stays one of the
   sender's own files; only the people in the chat can open it. "Ask Copilot"
   keeps a copy in your own files first, because Copilot only reads your files. */

export const CHAT_FILE_TYPES = '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp';
const ACCEPTED = /\.(pdf|docx|txt|md|png|jpe?g|webp)$/i;
const MAX = 10 * 1024 * 1024;

const size = (n) => (n >= 1024 * 1024 ? `${(n / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const kindOf = (name) => (name.match(/\.(\w+)$/)?.[1] || 'file').toUpperCase();

/* Upload a file for a chat; returns the new file, or null (having said why). */
export async function uploadChatFile(file, notify) {
  if (!file) return null;
  if (!ACCEPTED.test(file.name || '')) {
    notify?.('You can attach PDF, Word (.docx), .txt, .md and PNG, JPG or WebP images.');
    return null;
  }
  if (file.size > MAX) { notify?.('Files can be up to 10 MB.'); return null; }
  try {
    return await api.uploadFile(file, { useForMatching: false });
  } catch (e) { notify?.(e.message); return null; }
}

/* The 📎 button. */
export function AttachButton({ disabled, onPick, label = 'Attach' }) {
  return (
    <label className={`btn quiet sm chat-attach${disabled ? ' disabled' : ''}`} title="Attach a file or image (up to 10 MB)">
      <Icon name="paperclip" size={16} /> {label}
      <input type="file" hidden accept={CHAT_FILE_TYPES} disabled={disabled}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onPick(f); }} />
    </label>
  );
}

/* Drop a file anywhere on the chat; paste one into the box. */
export function useFileDrop(onFile) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
  return {
    over,
    dropProps: {
      onDragEnter: (e) => { if (hasFiles(e)) { e.preventDefault(); depth.current += 1; setOver(true); } },
      onDragOver: (e) => { if (hasFiles(e)) e.preventDefault(); },
      onDragLeave: () => { depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); },
      onDrop: (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        onFile(e.dataTransfer.files?.[0]);
      },
    },
    onPaste: (e) => {
      const item = [...(e.clipboardData?.items || [])].find((i) => i.kind === 'file');
      if (item) { e.preventDefault(); onFile(item.getAsFile()); }
    },
  };
}

function ChatImage({ fileId, load, name }) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  // Fetched once per file, however often the chat re-renders.
  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    let made = '';
    loader.current().then((u) => { made = u; setUrl(u); }).catch(() => setFailed(true));
    return () => { if (made) URL.revokeObjectURL(made); };
  }, [fileId]);
  return url ? <a href={url} target="_blank" rel="noopener noreferrer"><img className="grp-img" src={url} alt={name} /></a>
    : <div className="grp-img ph">{failed ? 'This image is no longer available' : 'Loading image…'}</div>;
}

/* What a message shows for its attachment. `loadImage`, `download` and `save`
   come from the chat it's in; `mine` when you sent it (it's already yours). */
export function ChatAttachment({ file, mine, loadImage, download, save, notify }) {
  const [busy, setBusy] = useState('');
  if (!file) return null;
  async function run(what, fn) {
    setBusy(what);
    try { await fn(); } catch (e) { notify?.(e.message); } finally { setBusy(''); }
  }
  const ask = () => run('ask', async () => { const r = await save(); askCopilotAbout(r.file_id); });
  const keep = () => run('save', async () => { await save(); notify?.('Saved to your files'); });
  if (file.is_image) {
    return (
      <div className="chat-file-img">
        <ChatImage fileId={file.id} load={loadImage} name={file.filename} />
        <div className="chat-file-actions">
          <button className="linklike" disabled={!!busy} onClick={() => run('dl', download)}>Download</button>
          <button className="linklike" disabled={!!busy} onClick={ask}>{busy === 'ask' ? 'Opening…' : 'Ask Copilot'}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="chat-file">
      <span className="chat-file-kind" aria-hidden="true">{kindOf(file.filename)}</span>
      <span className="chat-file-main">
        <b title={file.filename}>{file.filename}</b>
        <span className="muted-sm">{size(file.size_bytes)}</span>
      </span>
      <span className="chat-file-actions">
        <button className="linklike" disabled={!!busy} onClick={() => run('dl', download)}>{busy === 'dl' ? 'Downloading…' : 'Download'}</button>
        {!mine && <button className="linklike" disabled={!!busy} onClick={keep}>{busy === 'save' ? 'Saving…' : 'Save to my files'}</button>}
        <button className="linklike" disabled={!!busy} onClick={ask}>{busy === 'ask' ? 'Opening…' : 'Ask Copilot'}</button>
      </span>
    </div>
  );
}

/* The file waiting to go with the next message. */
export function PendingFile({ file, onRemove }) {
  if (!file) return null;
  return (
    <div className="chat-pending">
      <Icon name="paperclip" size={14} /> <b>{file.filename}</b> <span className="muted-sm">{size(file.size_bytes)}</span>
      <button className="linklike" aria-label="Remove the file" onClick={onRemove}>×</button>
    </div>
  );
}
